/**
 * AcuityServicesPanel - inline (non-modal) sync panel for the Settings - Services
 * sub-tab. Per spec (Iter 9.49+):
 *   - Acuity services SYNC to Host Services (text is copied over, editable in
 *     Phygital). Master Services are mapped to HOST Services, NOT directly to
 *     Acuity. So this panel: pulls Acuity catalog, lets host pick which to
 *     copy into Host Services (provider_services), with optional rename.
 *   - When a client books on Acuity (welcome page), the matched
 *     acuity_appointment_type_id wires them straight back to the right Host
 *     Service in Phygital and routes them to the intake preferences flow.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  CheckCircle2,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AcuityServicesPanel({ eventId, onAfterSync, onConnect }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phygital, setPhygital] = useState([]);
  const [acuity, setAcuity] = useState([]);
  const [connected, setConnected] = useState(false);
  // mappings: { [acuity_id]: { action: 'match'|'import'|'skip', phygital_service_id?: string } }
  const [mappings, setMappings] = useState({});

  const loadPreview = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/acuity/sync-services-preview`,
        { event_id: eventId },
        { headers: authHeaders(), withCredentials: true }
      );
      const acuityRows = res.data?.acuity_services || [];
      const phygicalRows = res.data?.phygital_services || [];
      setAcuity(acuityRows);
      setPhygital(phygicalRows);
      setConnected(!!res.data?.acuity_connected);
      // Default action per row: auto-match by name, else import
      const initial = {};
      acuityRows.forEach((a) => {
        const exact = phygicalRows.find(
          (p) => p.name?.toLowerCase().trim() === a.name?.toLowerCase().trim()
        );
        initial[a.id] = exact
          ? { action: "match", phygital_service_id: exact.service_id }
          : { action: "import" };
      });
      setMappings(initial);
    } catch {
      toast.error("Could not load Acuity services");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const updateMapping = (acuityId, patch) =>
    setMappings((prev) => ({ ...prev, [acuityId]: { ...(prev[acuityId] || {}), ...patch } }));

  const confirm = async () => {
    setSaving(true);
    try {
      const m = acuity.map((a) => ({
        action: mappings[a.id]?.action || "skip",
        acuity_service: a,
        phygital_service_id: mappings[a.id]?.phygital_service_id,
      }));
      const res = await axios.post(
        `${API}/acuity/sync-services-confirm`,
        { event_id: eventId, mappings: m },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success(
        `${res.data.matched} matched, ${res.data.imported} copied to Host Services, ${res.data.skipped} skipped`
      );
      onAfterSync && onAfterSync();
      // Re-pull to reflect new matches as "Copied"
      await loadPreview();
    } catch (e) {
      toast.error("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const counts = useMemo(() => {
    const c = { match: 0, import: 0, skip: 0 };
    Object.values(mappings).forEach((m) => {
      if (m?.action) c[m.action] = (c[m.action] || 0) + 1;
    });
    return c;
  }, [mappings]);

  if (!connected) {
    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="acuity-services-not-connected">
        <CardContent className="p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium">Acuity is not connected</p>
            <p className="text-xs text-gray-600 mt-1">
              Connect Acuity in Settings - Tech Stack to pull your service catalog. Once connected, you don't need to reconnect.
            </p>
            {onConnect && (
              <Button size="sm" className="mt-3" onClick={onConnect} data-testid="acuity-services-connect-btn">
                Open Tech Stack
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="acuity-services-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold">Acuity service catalog</h4>
          <p className="text-xs text-gray-500">
            Copy your Acuity services into Host Services. Phygital is your source of truth - you can edit the names and details after copying.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={loadPreview} disabled={loading} data-testid="acuity-services-refresh">
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            <span className="ml-1.5 text-xs">Refresh from Acuity</span>
          </Button>
          <Button
            size="sm"
            onClick={confirm}
            disabled={saving || acuity.length === 0}
            data-testid="acuity-services-apply"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Apply ({counts.match} match, {counts.import} copy, {counts.skip} skip)
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : acuity.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No services found in your Acuity account.</p>
      ) : (
        <div className="space-y-2">
          {acuity.map((a) => {
            const map = mappings[a.id] || {};
            return (
              <Card key={a.id} className="border-gray-200" data-testid={`acuity-svc-row-${a.id}`}>
                <CardContent className="p-3">
                  <div className="grid grid-cols-12 gap-3 items-start">
                    <div className="col-span-5">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[9px] bg-purple-50 border-purple-200 text-purple-700">
                          ACUITY
                        </Badge>
                        <p className="font-medium text-sm">{a.name}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 line-clamp-2">{a.description}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {a.duration} min - ${a.price || 0}
                      </p>
                    </div>

                    <div className="col-span-3 space-y-1.5">
                      <Select
                        value={map.action}
                        onValueChange={(v) => updateMapping(a.id, { action: v })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`svc-action-${a.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="match">
                            <Link2 className="w-3 h-3 inline mr-1" /> Match existing
                          </SelectItem>
                          <SelectItem value="import">
                            <Plus className="w-3 h-3 inline mr-1" /> Copy as new Host Service
                          </SelectItem>
                          <SelectItem value="skip">
                            <X className="w-3 h-3 inline mr-1" /> Skip
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {map.action === "match" && (
                        <Select
                          value={map.phygital_service_id}
                          onValueChange={(v) => updateMapping(a.id, { phygital_service_id: v })}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`svc-pick-${a.id}`}>
                            <SelectValue placeholder="Pick Host Service" />
                          </SelectTrigger>
                          <SelectContent>
                            {phygital.map((p) => (
                              <SelectItem key={p.service_id} value={p.service_id}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <div className="col-span-4">
                      {map.action === "match" &&
                        map.phygital_service_id &&
                        (() => {
                          const p = phygital.find((s) => s.service_id === map.phygital_service_id);
                          if (!p) return null;
                          return (
                            <div className="bg-green-50 border border-green-200 rounded-md p-2">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <Badge variant="outline" className="text-[9px] bg-green-50 border-green-300 text-green-700">
                                  HOST SERVICE
                                </Badge>
                                <CheckCircle2 className="w-3 h-3 text-green-600" />
                              </div>
                              <p className="text-xs font-medium">{p.name}</p>
                              <p className="text-[10px] text-gray-500">
                                ${p.price} - {p.duration_minutes} min
                              </p>
                            </div>
                          );
                        })()}
                      {map.action === "import" && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                          <p className="text-[10px] text-blue-700">
                            Copies to Host Services. You can rename and edit afterwards.
                          </p>
                        </div>
                      )}
                      {map.action === "skip" && (
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                          <p className="text-[10px] text-gray-500">Will not be copied.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
